import { api, tagTypes } from "./api";
import type {
  Occupation,
  Category,
  Bank,
  VariableFee,
  IntervalFeeType,
} from "../features/miscSlice";

interface pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// API Response types
export interface OccupationsResponse {
  success: boolean;
  message: string;
  payload: {
    data: Occupation[];
    pagination: pagination;
  };
}

export interface CategoriesResponse {
  success: boolean;
  message: string;
  payload: {
    data: Category[];
  };
}

export interface BanksResponse {
  success: boolean;
  message: string;
  payload: {
    data: Bank[];
    pagination: pagination;
  };
}

export interface VariableFeeResponse {
  success: boolean;
  message: string;
  payload: VariableFee;
}

export interface AddOccupationRequest {
  name: string;
  category_slug: string;
  base_fee: string;
  icon: File;
}

export interface EditOccupationRequest {
  id: string;
  data: {
    name?: string;
    category_slug?: string;
    base_fee?: string;
    icon?: File;
  };
}

export interface AddCategoryRequest {
  name: string;
  slug: string;
}

export interface EditCategoryRequest {
  id: string;
  data: {
    name?: string;
    slug?: string;
  };
}

export interface SetVariableFeeRequest {
  amount_per_km: number;
  time_variation: IntervalFeeType[];
}

export interface OccupationAnalyticsResponse {
  success: boolean;
  message: string;
  payload: {
    data: {
      occupation: string;
      totalVendors: number;
      genderDistribution: {
        male: number;
        female: number;
      };
      ageGroups: {
        "18–24": number;
        "25–34": number;
        "35–45": number;
        "45+": number;
      };
      countries: Array<{
        country: string;
        count: number;
      }>;
    };
  };
}

type PaginationParams = {
  page?: number;
  limit?: number;
  search?: string;
};

// Inject endpoints into the main API
export const miscApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Occupations
    getOccupations: builder.query<OccupationsResponse, PaginationParams, void>({
      query: ({ page = 1, limit = 10,  search = 'yo' }) => ({
        url: "admin/v1/misc/occupations",
        params: { page, limit, search },
      }),
      providesTags: [tagTypes.Occupation],
    }),

    addOccupation: builder.mutation<any, FormData>({
      query: (data) => ({
        url: "admin/v1/misc/occupation",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [tagTypes.Occupation],
    }),

    editOccupation: builder.mutation<any, EditOccupationRequest>({
      query: ({ id, data }) => ({
        url: `admin/v1/misc/occupation/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [tagTypes.Occupation],
    }),

    deleteOccupation: builder.mutation<any, string>({
      query: (id) => ({
        url: `admin/v1/misc/occupation/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [tagTypes.Occupation],
    }),

    // Categories
    getCategories: builder.query<CategoriesResponse, void>({
      query: () => ({
        url: "admin/v1/misc/occupation/category",
      }),
      providesTags: [tagTypes.Bank],
    }),

    // Categories
    // getCategories: builder.query<CategoriesResponse, PaginationParams, void>({
    //   query: ({ page = 1, limit = 10 }) => ({
    //     url: "admin/v1/misc/occupation/category",
    //     params: { page, limit },
    //   }),
    //   providesTags: [tagTypes.Bank],
    // }),

    addCategory: builder.mutation<any, AddCategoryRequest>({
      query: (data) => ({
        url: "admin/v1/misc/occupation/category",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [tagTypes.Category],
    }),

    editCategory: builder.mutation<any, EditCategoryRequest>({
      query: ({ id, data }) => ({
        url: `admin/v1/misc/occupation/category/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [tagTypes.Category],
    }),

    deleteCategory: builder.mutation<any, string>({
      query: (id) => ({
        url: `admin/v1/misc/occupation/category/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [tagTypes.Category],
    }),

    // Banks
    getBanks: builder.query<BanksResponse, PaginationParams, void>({
      query: ({ page = 1, limit = 10 }) => ({
        url: "admin/v1/misc/banks",
        params: { page, limit },
      }),
      providesTags: [tagTypes.Bank],
    }),

    // Variable Fee
    getVariableFee: builder.query<VariableFeeResponse, void>({
      query: () => "booking/v1/admin/variable-fee",
      providesTags: [tagTypes.VariableFee],
    }),

    setVariableFee: builder.mutation<any, SetVariableFeeRequest>({
      query: (data) => ({
        url: "booking/v1/admin/variable-fee",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [tagTypes.VariableFee],
    }),

    // Cache
    deleteCache: builder.mutation<any, void>({
      query: () => ({
        url: "admin/v1/cache",
        method: "DELETE",
      }),
      invalidatesTags: [tagTypes.Misc],
    }),

    // Export vendors
    exportVendors: builder.query<any, void>({
      query: () => ({
        url: "admin/v1/misc/export/vendors",
        method: "GET",
      }),
      providesTags: [tagTypes.Misc],
    }),

    // Occupation Analytics
    getOccupationAnalytics: builder.query<OccupationAnalyticsResponse, string>({
      query: (name) =>
        `admin/v1/misc/occupation/analytics/${encodeURIComponent(name)}`,
      providesTags: [tagTypes.Occupation],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetOccupationsQuery,
  useLazyGetOccupationsQuery,
  useAddOccupationMutation,
  useEditOccupationMutation,
  useDeleteOccupationMutation,
  useGetCategoriesQuery,
  useLazyGetCategoriesQuery,
  useAddCategoryMutation,
  useEditCategoryMutation,
  useDeleteCategoryMutation,
  useGetBanksQuery,
  useLazyGetBanksQuery,
  useGetVariableFeeQuery,
  useLazyGetVariableFeeQuery,
  useSetVariableFeeMutation,
  useDeleteCacheMutation,
  useExportVendorsQuery,
  useLazyExportVendorsQuery,
  useGetOccupationAnalyticsQuery,
  useLazyGetOccupationAnalyticsQuery,
} = miscApi;
